import React, { useMemo, useState } from "react";

// 1. Made 'index' an optional second parameter in render to prevent breaking other files
export interface Column<T> {
  key: keyof T | string; 
  label: string;
  render?: (item: T, index?: number) => React.ReactNode; // <-- Added optional index
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onView?: (item: T) => void;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
}

export default function DataTable<T extends { id: number }>({
  data,
  columns,
  onView,
  onEdit,
  onDelete,
}: DataTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof T; direction: "asc" | "desc" } | null>(null);
  const [filter, setFilter] = useState("");

  // Sorting
  const sortedData = useMemo(() => {
    let sortable = [...data];
    if (sortConfig) {
      sortable.sort((a, b) => {
        const valA = a[sortConfig.key];
        const valB = b[sortConfig.key];
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortable;
  }, [data, sortConfig]);

  // Filtering
  const filteredData = useMemo(() => {
    return sortedData.filter((item) =>
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(filter.toLowerCase())
      )
    );
  }, [sortedData, filter]);

  const requestSort = (key: keyof T | string) => {
    if (typeof key === 'string' && !(key in (data[0] || {}))) return;

    let direction: "asc" | "desc" = "asc";
    const sortKey = key as keyof T;
    if (sortConfig && sortConfig.key === sortKey && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: sortKey, direction });
  };

  const getSortIndicator = (key: keyof T | string) => {
    if (!sortConfig || sortConfig.key !== key) return " ⇅";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0 fw-bold text-muted">Records</h5>
        <div className="input-group w-25">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search in table..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light text-muted">
              <tr>
                {columns.map((col) => (
                  <th
                    key={String(col.key)}
                    onClick={() => requestSort(col.key)}
                    className="cursor-pointer small text-uppercase"
                    style={{ cursor: 'pointer' }}
                  >
                    {col.label}
                    <span className="text-primary">{getSortIndicator(col.key)}</span>
                  </th>
                ))}
                {(onView || onEdit || onDelete) && <th className="text-center small text-uppercase">Action</th>}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((item, rowIndex) => ( // <-- Grabbed rowIndex here
                <tr key={item.id}>
                  {columns.map((col) => (
                    <td key={String(col.key)}>
                      {/* 🔹 Logic: Pass item and the active rowIndex down to the render method */}
                      {col.render 
                        ? col.render(item, rowIndex) 
                        : String((item as any)[col.key] ?? '')
                      }
                    </td>
                  ))}
                  {(onView || onEdit || onDelete) && (
                    <td className="text-center">
                      <div className="d-flex justify-content-center gap-1">
                        {onView && (
                          <button className="btn btn-outline-info btn-sm" onClick={() => onView(item)}>
                            View
                          </button>
                        )}
                        {onEdit && (
                          <button className="btn btn-outline-success btn-sm" onClick={() => onEdit(item)}>
                            Edit
                          </button>
                        )}
                        {onDelete && (
                          <button className="btn btn-outline-danger btn-sm" onClick={() => onDelete(item)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {filteredData.length === 0 && (
            <div className="text-center py-5">
              <p className="text-muted mb-0">No matching records found.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}